<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, vue/html-closing-bracket-newline, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref } from 'vue';
import ImudeiRuntimeActionPanel from './ImudeiRuntimeActionPanel.vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, refresh, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'proposals'),
    screen: computed(() => 'detail'),
  })
);

const actionsOpen = ref(false);

const asArray = value => (Array.isArray(value) ? value : []);
const valueAt = (record, path) =>
  path.split('.').reduce((value, key) => value?.[key], record);
const firstValue = (record, ...paths) => {
  const value = paths
    .map(path => valueAt(record, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return value ?? '';
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const readable = value => {
  const text = String(value || '').replaceAll('_', ' ').trim();
  return text ? `${text[0].toLocaleUpperCase('pt-BR')}${text.slice(1)}` : '—';
};
const money = (value, currency) => {
  if (value === undefined || value === null || value === '') return '—';
  const amount = typeof value === 'object' ? value.amount ?? value.value : value;
  const number = Number(amount);
  if (!Number.isFinite(number)) return String(amount);
  if (!currency) return new Intl.NumberFormat('pt-BR').format(number);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(number);
};
const date = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
    parsed
  );
};
const dateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const progressClass = percentage => {
  const value = Number(percentage);
  if (!Number.isFinite(value) || value <= 0) return 'w-0';
  if (value < 25) return 'w-1/4';
  if (value < 50) return 'w-1/3';
  if (value < 75) return 'w-1/2';
  if (value < 100) return 'w-3/4';
  return 'w-full';
};
const stageTone = stage => {
  const state = String(stage?.state || stage?.status || '').toLowerCase();
  if (stage?.current || /atual|current|andamento|in_progress/.test(state)) {
    return 'border-[#5a24c7] bg-violet-50 text-[#5a24c7]';
  }
  if (/concl|complet|done|final/.test(state)) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-600';
  }
  return 'border-[#dfe3ef] bg-white text-[#8791ab]';
};
const statusTone = status => {
  const value = String(status || '').toLowerCase();
  if (/aceit|conclu|final/.test(value)) return 'bg-emerald-50 text-emerald-700';
  if (/recus|declin|retir|cancel/.test(value)) return 'bg-rose-50 text-rose-700';
  if (/document|contrat/.test(value)) return 'bg-sky-50 text-sky-700';
  return 'bg-violet-50 text-[#5a24c7]';
};

const source = computed(() => model.detail?.source || {});
const revision = computed(
  () => source.value.current_revision || source.value.currentRevision || {}
);
const property = computed(
  () => source.value.imovel || source.value.property || source.value.listing || {}
);
const contact = computed(
  () => source.value.contact || source.value.buyer || source.value.client || {}
);
const broker = computed(
  () => source.value.user || source.value.broker || source.value.responsible || {}
);
const currency = computed(() =>
  firstValue(revision.value, 'currency') || firstValue(source.value, 'currency')
);
const offerValue = (...paths) => {
  const revisionValue = firstValue(revision.value, ...paths);
  return revisionValue === '' ? firstValue(source.value, ...paths) : revisionValue;
};
const offerAmount = computed(() =>
  offerValue('offer_amount', 'terms.offer_amount', 'amount')
);
const propertyAmount = computed(() =>
  firstValue(
    source.value,
    'imovel_valor_venda',
    'imovel.valor_venda',
    'property.price_amount',
    'listing.price_amount'
  )
);
const entryAmount = computed(() =>
  offerValue(
    'terms.earnest_payment_amount',
    'terms.entry_amount',
    'earnest_payment_amount',
    'entry_amount'
  )
);
const financedAmount = computed(() =>
  offerValue('terms.financed_amount', 'financed_amount')
);
const paymentKind = computed(() =>
  offerValue('terms.payment_kind', 'payment_kind')
);
const expiresAt = computed(() =>
  offerValue('expires_at', 'terms.expires_at') ||
  firstValue(source.value, 'expires_at')
);
const proposalTitle = computed(() =>
  firstValue(source.value, 'title', 'imovel_titulo', 'imovel.titulo', 'reference') ||
  'Proposta não retornada'
);
const proposalCode = computed(
  () => firstValue(source.value, 'reference', 'code', 'id') || model.detail?.id || '—'
);
const proposalStatus = computed(
  () =>
    firstValue(
      source.value,
      'macro_status_label',
      'status_label',
      'macro_status',
      'status'
    ) || 'Status não retornado'
);
const propertyAddress = computed(
  () =>
    firstValue(
      source.value,
      'imovel_endereco',
      'imovel.endereco',
      'imovel.address',
      'property.address',
      'listing.address'
    ) || 'Endereço não retornado'
);
const propertyType = computed(() =>
  firstValue(property.value, 'tipo', 'type', 'property_type') ||
  firstValue(source.value, 'kind') ||
  'Tipo não retornado'
);
const propertyArea = computed(
  () => firstValue(property.value, 'area', 'area_total', 'area_privativa', 'metragem') || '—'
);
const propertyBedrooms = computed(
  () => firstValue(property.value, 'bedrooms', 'quartos', 'dormitorios') || '—'
);
const propertyParking = computed(
  () => firstValue(property.value, 'parking_spaces', 'vagas', 'garages') || '—'
);
const contactName = computed(
  () => firstValue(contact.value, 'name', 'full_name') || source.value.contact_name || 'Cliente não retornado'
);
const contactPhone = computed(
  () =>
    firstValue(contact.value, 'phone_number', 'phone', 'telephone') ||
    source.value.contact_phone_number ||
    'Telefone não retornado'
);
const brokerName = computed(
  () => firstValue(broker.value, 'name', 'full_name') || source.value.user_name || 'Corretor não retornado'
);
const brokerPhone = computed(
  () => firstValue(broker.value, 'phone_number', 'phone', 'telephone') || 'Telefone não retornado'
);
const discount = computed(() => {
  const offer = Number(offerAmount.value);
  const listed = Number(propertyAmount.value);
  if (!Number.isFinite(offer) || !Number.isFinite(listed) || listed <= 0) return '';
  return ((1 - offer / listed) * 100).toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  });
});

const rawTimeline = computed(() => asArray(source.value.timeline));
const stageSlots = computed(() => {
  const slots = rawTimeline.value.slice(0, 4).map((stage, index) => ({
    id: String(stage.id || stage.key || index),
    stage,
    unavailable: false,
  }));

  while (slots.length < 4) {
    slots.push({
      id: `stage-unavailable-${slots.length}`,
      stage: null,
      unavailable: true,
    });
  }

  return slots;
});
const completedStageCount = computed(() =>
  rawTimeline.value.filter(stage =>
    /concl|complet|done|final/.test(
      String(stage?.state || stage?.status || '').toLowerCase()
    )
  ).length
);
const currentStage = computed(
  () =>
    rawTimeline.value.find(stage => stage?.current) ||
    rawTimeline.value.find(stage =>
      /atual|current|andamento|in_progress/.test(
        String(stage?.state || stage?.status || '').toLowerCase()
      )
    ) ||
    null
);
const progressPercentage = computed(() => {
  if (!rawTimeline.value.length) return 0;
  const current = currentStage.value ? 1 : 0;
  return Math.round(
    ((completedStageCount.value + current) / rawTimeline.value.length) * 100
  );
});

const participantRows = computed(() => {
  const candidates = [
    contactName.value !== 'Cliente não retornado' && {
      id: contact.value.id || source.value.contact_id,
      name: contactName.value,
      phone: contactPhone.value,
      role: 'Cliente',
    },
    brokerName.value !== 'Corretor não retornado' && {
      id: broker.value.id || source.value.user_id,
      name: brokerName.value,
      phone: brokerPhone.value,
      role: 'Corretor responsável',
    },
    ...asArray(source.value.participants),
  ].filter(Boolean);

  return candidates
    .map((participant, index) => {
      const name = firstValue(participant, 'name', 'full_name', 'contact.name');
      if (!name) return null;

      return {
        id: String(participant.id || `${name}-${index}`),
        name,
        phone:
          firstValue(participant, 'phone_number', 'phone', 'contact.phone_number') ||
          'Telefone não retornado',
        role:
          firstValue(participant, 'role', 'party_role_label', 'kind') ||
          'Participante',
      };
    })
    .filter(Boolean)
    .filter(
      (participant, index, records) =>
        records.findIndex(record => record.name === participant.name) === index
    )
    .slice(0, 3);
});
const participantSlots = computed(() => {
  const slots = participantRows.value.map(participant => ({
    id: participant.id,
    participant,
    unavailable: false,
  }));

  while (slots.length < 3) {
    slots.push({
      id: `participant-unavailable-${slots.length}`,
      participant: null,
      unavailable: true,
    });
  }

  return slots;
});
const includedItems = computed(() => {
  const values = firstValue(
    revision.value,
    'terms.included_items',
    'terms.items_included'
  );
  return asArray(values)
    .map((item, index) => ({
      id: String(item?.id || item?.code || index),
      label:
        typeof item === 'string'
          ? item
          : firstValue(item, 'label', 'name', 'title', 'description'),
    }))
    .filter(item => item.label)
    .slice(0, 5);
});
const includedSlots = computed(() => {
  const slots = includedItems.value.map(item => ({
    id: item.id,
    item,
    unavailable: false,
  }));

  while (slots.length < 5) {
    slots.push({
      id: `included-unavailable-${slots.length}`,
      item: null,
      unavailable: true,
    });
  }

  return slots;
});
const previousRevision = computed(() => {
  const direct = source.value.previous_revision || source.value.previousRevision;
  if (direct) return direct;

  const currentId = firstValue(revision.value, 'id');
  return asArray(source.value.negotiation_timeline)
    .filter(item => firstValue(item, 'offer_amount', 'amount') !== '')
    .filter(item => String(firstValue(item, 'id')) !== String(currentId))
    .at(-1) || null;
});
const previousAmount = computed(() =>
  previousRevision.value
    ? firstValue(previousRevision.value, 'offer_amount', 'amount')
    : ''
);
const previousEntryAmount = computed(() =>
  previousRevision.value
    ? firstValue(
        previousRevision.value,
        'terms.earnest_payment_amount',
        'terms.entry_amount',
        'earnest_payment_amount',
        'entry_amount'
      )
    : ''
);
const previousPaymentKind = computed(() =>
  previousRevision.value
    ? firstValue(previousRevision.value, 'terms.payment_kind', 'payment_kind')
    : ''
);
const previousExpiresAt = computed(() =>
  previousRevision.value
    ? firstValue(previousRevision.value, 'expires_at', 'terms.expires_at')
    : ''
);
const moneyDifference = (current, previous) => {
  const difference = Number(current) - Number(previous);
  if (!Number.isFinite(difference)) return 'Indisponível';
  return `${difference > 0 ? '+' : ''}${money(difference, currency.value)}`;
};
const daysDifference = (current, previous) => {
  const currentDate = new Date(current);
  const previousDate = new Date(previous);
  if (Number.isNaN(currentDate.valueOf()) || Number.isNaN(previousDate.valueOf())) {
    return 'Indisponível';
  }
  const difference = Math.round(
    (currentDate.valueOf() - previousDate.valueOf()) / 86400000
  );
  return `${difference > 0 ? '+' : ''}${difference} dias`;
};
const activitySlots = computed(() => {
  const slots = model.history.slice(-5).reverse().map(activity => ({
    activity,
    id: activity.id,
    unavailable: false,
  }));

  while (slots.length < 5) {
    slots.push({
      activity: null,
      id: `activity-unavailable-${slots.length}`,
      unavailable: true,
    });
  }

  return slots;
});
const hasCounterAction = computed(() =>
  model.actionOptions.some(action => action.name === 'counter')
);
const updateActionField = ({ key, value }) => {
  model.form[key] = value;
};
const runAction = async action => {
  await model.runAction(action);
};
</script>

<template>
  <main class="w-full pb-6 pl-12 pr-6 pt-0 text-[#101958]">
    <section
      v-if="!model.detail"
      class="grid min-h-[600px] place-items-center rounded-2xl border border-[#e8e4ef] bg-white text-center"
    >
      <span>
        <i class="i-lucide-file-question mx-auto size-8 text-[#8e99b5]" />
        <strong class="mt-3 block text-[12px]">Proposta indisponível</strong>
        <small class="mt-1 block text-[9px] text-[#69759c]">
          A API não retornou a proposta selecionada.
        </small>
        <button
          class="mx-auto mt-4 flex items-center gap-2 rounded-lg border border-[#ded5ee] px-4 py-2 text-[9px] font-semibold text-[#5a24c7]"
          type="button"
          @click="refresh"
        >
          <i class="i-lucide-refresh-cw size-3" />Atualizar
        </button>
      </span>
    </section>

    <template v-else>
      <section class="h-[192px] overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
        <div class="grid h-full grid-cols-[190px_minmax(0,1.55fr)_0.85fr_0.85fr_0.95fr] gap-5">
          <span class="relative grid h-[154px] place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-[#40556b] via-[#8393a2] to-[#dfc5ae] text-white">
            <i class="i-lucide-building-2 size-9" />
            <small class="absolute bottom-3 left-3 rounded-full bg-white/90 px-2 py-1 text-[7px] font-semibold text-[#101958]">
              {{ readable(propertyType) }}
            </small>
          </span>

          <div class="min-w-0 py-2">
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <h1 class="truncate text-[23px] font-bold tracking-[-0.035em]">{{ proposalTitle }}</h1>
              <span class="rounded-full px-3 py-1 text-[8px] font-semibold" :class="statusTone(proposalStatus)">
                {{ readable(proposalStatus) }}
              </span>
            </div>
            <p class="mt-1 text-[8px] text-[#69759c]">
              {{ proposalCode }} · Criada em {{ dateTime(firstValue(source, 'created_at', 'submitted_at', 'proposal_date')) }}
            </p>
            <p class="mt-3 flex items-center gap-2 truncate text-[10px] font-semibold">
              <i class="i-lucide-map-pin size-4 shrink-0 text-[#5a24c7]" />{{ propertyAddress }}
            </p>
            <div class="mt-3 flex gap-5 text-[8px] text-[#627097]">
              <span class="flex items-center gap-1"><i class="i-lucide-ruler size-3.5" />{{ propertyArea }} m²</span>
              <span class="flex items-center gap-1"><i class="i-lucide-bed-double size-3.5" />{{ propertyBedrooms }} quartos</span>
              <span class="flex items-center gap-1"><i class="i-lucide-car-front size-3.5" />{{ propertyParking }} vagas</span>
            </div>
          </div>

          <div class="border-l border-[#eeeaf3] pt-5 pl-5">
            <small class="text-[8px] text-[#69759c]">Cliente</small>
            <div class="mt-3 flex items-center gap-3">
              <i class="grid size-11 place-items-center rounded-full border-2 border-cyan-400 bg-cyan-50 text-[10px] font-bold text-cyan-700">
                {{ initials(contactName) }}
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">{{ contactName }}</strong>
                <small class="mt-1 block truncate text-[8px] text-[#69759c]">{{ contactPhone }}</small>
              </span>
            </div>
          </div>

          <div class="border-l border-[#eeeaf3] pt-5 pl-5">
            <small class="text-[8px] text-[#69759c]">Corretor responsável</small>
            <div class="mt-3 flex items-center gap-3">
              <i class="grid size-11 place-items-center rounded-full border-2 border-violet-500 bg-violet-50 text-[10px] font-bold text-[#5a24c7]">
                {{ initials(brokerName) }}
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">{{ brokerName }}</strong>
                <small class="mt-1 block truncate text-[8px] text-[#69759c]">{{ brokerPhone }}</small>
              </span>
            </div>
          </div>

          <div class="border-l border-[#eeeaf3] py-2 pl-5">
            <small class="text-[8px] text-[#69759c]">Valor da proposta</small>
            <strong class="mt-2 block text-[22px] font-bold tracking-[-0.035em] text-[#5520ce]">
              {{ money(offerAmount, currency) }}
            </strong>
            <div class="mt-3 space-y-2 border-t border-[#eeeaf3] pt-3 text-[8px]">
              <p class="flex justify-between gap-2"><span class="text-[#69759c]">Valor anunciado</span><b>{{ money(propertyAmount, currency) }}</b></p>
              <p class="flex justify-between gap-2"><span class="text-[#69759c]">Desconto</span><b :class="discount ? 'text-emerald-600' : 'text-[#7b85a1]'">{{ discount ? `${discount}%` : 'Indisponível' }}</b></p>
              <p class="flex justify-between gap-2"><span class="text-[#69759c]">Válida até</span><b>{{ date(expiresAt) }}</b></p>
            </div>
          </div>
        </div>
      </section>

      <section class="mt-3 grid h-[78px] grid-cols-[minmax(0,1fr)_512px] gap-4">
        <div class="h-full rounded-2xl border border-[#ebe7f1] bg-white p-3 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
          <div class="grid h-full grid-cols-4 gap-2">
            <span
              v-for="slot in stageSlots"
              :key="slot.id"
              class="relative flex min-w-0 items-center gap-3"
            >
              <i
                class="grid size-10 shrink-0 place-items-center rounded-full border text-[14px]"
                :class="slot.unavailable ? 'border-[#dfe3ef] bg-[#faf9fc] text-[#9aa4bb]' : stageTone(slot.stage)"
              >
                <i v-if="slot.unavailable" class="i-lucide-circle-slash-2 size-4" />
                <i v-else-if="slot.stage.current" class="i-lucide-check size-4" />
                <i v-else class="i-lucide-circle-dot size-4" />
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">
                  {{ slot.unavailable ? 'Etapa indisponível' : readable(firstValue(slot.stage, 'label', 'title', 'key')) }}
                </strong>
                <small class="mt-1 block truncate text-[7px] text-[#7380a2]">
                  {{ slot.unavailable ? 'A API não retornou o cronograma.' : firstValue(slot.stage, 'description', 'subtitle', 'state', 'status') || 'Sem descrição retornada' }}
                </small>
              </span>
              <i v-if="slot.id !== stageSlots.at(-1)?.id" class="i-lucide-arrow-right absolute -right-1 top-4 size-4 text-[#cbd1df]" />
            </span>
          </div>
        </div>

        <div class="flex h-[56px] self-center gap-2 pl-2">
          <button
            class="flex-1 rounded-xl border border-[#a27aff] bg-white px-4 text-[9px] font-bold text-[#5520ce] disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!model.canEdit"
            type="button"
            @click="emit('navigate', 'edit')"
          >
            <i class="i-lucide-pencil mr-1 inline size-3.5" />Editar
          </button>
          <button
            class="flex-1 rounded-xl border border-[#d7c9ff] bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-4 text-[9px] font-bold text-white"
            type="button"
            @click="actionsOpen = !actionsOpen"
          >
            <i class="i-lucide-send mr-1 inline size-3.5" />{{ model.actionOptions.length ? `${model.actionOptions.length} ações` : 'Ações' }}
          </button>
          <button
            class="rounded-xl border border-[#e4d8f9] bg-[#f4efff] px-4 text-[9px] font-bold text-[#6b42c7]"
            type="button"
            @click="refresh"
          >
            <i class="i-lucide-refresh-cw size-3.5" />
          </button>
        </div>
      </section>

      <ImudeiRuntimeActionPanel
        v-if="actionsOpen"
        class="mt-4"
        :action-options="model.actionOptions"
        :can-counter-offer="hasCounterAction"
        :form="model.form"
        :saving="saving"
        :transition-options="model.transitionOptions"
        @run="runAction"
        @update-field="updateActionField"
      />

      <section class="mt-4 grid grid-cols-[minmax(0,1fr)_512px] gap-4">
        <div class="grid grid-cols-[1.07fr_1fr_0.82fr] gap-4">
          <article class="h-[310px] overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <h2 class="text-[11px] font-bold">Resumo da negociação</h2>
            <dl class="mt-4 space-y-3 text-[9px]">
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Valor da proposta</dt><dd class="font-bold text-[#5520ce]">{{ money(offerAmount, currency) }}</dd></div>
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Valor anunciado</dt><dd class="font-semibold">{{ money(propertyAmount, currency) }}</dd></div>
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Desconto</dt><dd :class="discount ? 'font-semibold text-emerald-600' : 'text-[#7a85a1]'">{{ discount ? `${discount}%` : 'Indisponível' }}</dd></div>
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Condição de pagamento</dt><dd class="max-w-[55%] truncate text-right font-semibold">{{ paymentKind ? readable(paymentKind) : 'Indisponível' }}</dd></div>
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Válida até</dt><dd class="font-semibold">{{ date(expiresAt) }}</dd></div>
              <div class="flex justify-between gap-3"><dt class="text-[#69759c]">Sinal / Reserva</dt><dd class="font-semibold">{{ money(entryAmount, currency) }}</dd></div>
            </dl>
            <p class="mt-4 rounded-xl bg-violet-50 px-3 py-2 text-[8px] leading-4 text-[#66509a]">
              <i class="i-lucide-info mr-1 inline size-3" />O contrato não retorna avaliação de flexibilidade da negociação.
            </p>
          </article>

          <article class="flex h-[310px] flex-col overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <h2 class="text-[11px] font-bold">Plano de pagamento</h2>
            <div class="mt-5 flex gap-3">
              <i class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"><i class="i-lucide-circle-dollar-sign size-3" /></i>
              <span>
                <strong class="block text-[9px]">Sinal / Reserva</strong>
                <b class="mt-1 block text-[10px]">{{ money(entryAmount, currency) }}</b>
                <small class="mt-1 block text-[7px] text-[#69759c]">{{ entryAmount === '' ? 'Valor não retornado' : 'Valor informado na revisão atual' }}</small>
              </span>
            </div>
            <div class="mt-5 flex gap-3">
              <i class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"><i class="i-lucide-wallet-cards size-3" /></i>
              <span>
                <strong class="block text-[9px]">Pagamento principal</strong>
                <b class="mt-1 block text-[10px]">{{ money(financedAmount || offerAmount, currency) }}</b>
                <small class="mt-1 block text-[7px] text-[#69759c]">{{ paymentKind ? readable(paymentKind) : 'Condição não retornada' }}</small>
              </span>
            </div>
            <div class="mt-auto flex justify-between border-t border-[#eeeaf3] pt-4 text-[9px]">
              <strong>Total</strong><b class="text-[#5520ce]">{{ money(offerAmount, currency) }}</b>
            </div>
          </article>

          <article class="h-[310px] overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <h2 class="text-[11px] font-bold">Itens incluídos</h2>
            <div
              v-for="slot in includedSlots"
              :key="slot.id"
              class="mt-4 flex items-center gap-3 text-[8px]"
            >
              <i
                class="grid size-4 shrink-0 place-items-center rounded-full"
                :class="slot.unavailable ? 'border border-[#cbd2e0] text-[#9aa4bb]' : 'bg-emerald-500 text-white'"
              >
                <i :class="slot.unavailable ? 'i-lucide-circle-slash-2 size-3' : 'i-lucide-check size-3'" />
              </i>
              <span :class="slot.unavailable ? 'text-[#8490ad]' : 'font-medium text-[#39415f]'">
                {{ slot.unavailable ? 'Item não retornado pela API' : slot.item.label }}
              </span>
            </div>
          </article>

          <article class="flex h-[276px] flex-col overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <header class="flex items-center justify-between">
              <h2 class="text-[11px] font-bold">Participantes</h2>
              <small class="text-[7px] text-[#7b85a1]">Dados da proposta</small>
            </header>
            <div
              v-for="slot in participantSlots"
              :key="slot.id"
              class="mt-4 flex items-center gap-3 border-b border-[#f0edf5] pb-3 last:border-b-0 last:pb-0"
            >
              <i
                class="grid size-9 shrink-0 place-items-center rounded-full text-[8px] font-bold"
                :class="slot.unavailable ? 'border border-dashed border-[#d9d4e4] bg-[#faf9fc] text-[#99a2b8]' : 'bg-violet-50 text-[#5a24c7]'"
              >
                {{ slot.unavailable ? '—' : initials(slot.participant.name) }}
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{ slot.unavailable ? 'Participante indisponível' : slot.participant.name }}</strong>
                <small class="mt-1 block truncate text-[7px] text-[#69759c]">{{ slot.unavailable ? 'A API não retornou outro participante.' : slot.participant.phone }}</small>
              </span>
              <span
                class="rounded-full px-2 py-1 text-[7px]"
                :class="slot.unavailable ? 'bg-slate-50 text-slate-500' : 'bg-violet-50 text-[#5a24c7]'"
              >
                {{ slot.unavailable ? 'Sem dados' : readable(slot.participant.role) }}
              </span>
            </div>
            <p class="mt-auto border-t border-[#f0edf5] pt-3 text-center text-[8px] text-[#8490ad]">Adição de participantes indisponível nesta tela.</p>
          </article>

          <article class="col-span-2 flex h-[276px] flex-col overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <h2 class="text-[11px] font-bold">Comparativo com contraproposta</h2>
            <div class="mt-4 grid grid-cols-[0.9fr_1fr_1fr_0.75fr] gap-2 text-[7px] text-[#7b85a2]">
              <span>Item</span><span>Proposta atual</span><span>Última contraproposta</span><span>Diferença</span>
            </div>
            <div class="mt-3 grid grid-cols-[0.9fr_1fr_1fr_0.75fr] gap-2 border-t border-[#f0edf5] pt-3 text-[8px]"><span>Valor</span><b>{{ money(offerAmount, currency) }}</b><b>{{ money(previousAmount, currency) }}</b><b :class="previousAmount === '' ? 'text-[#8490ad]' : 'text-emerald-600'">{{ moneyDifference(offerAmount, previousAmount) }}</b></div>
            <div class="mt-3 grid grid-cols-[0.9fr_1fr_1fr_0.75fr] gap-2 text-[8px]"><span>Condição</span><b>{{ paymentKind ? readable(paymentKind) : '—' }}</b><b>{{ previousPaymentKind ? readable(previousPaymentKind) : '—' }}</b><b class="text-[#8490ad]">{{ previousPaymentKind ? 'Retornado' : 'Indisponível' }}</b></div>
            <div class="mt-3 grid grid-cols-[0.9fr_1fr_1fr_0.75fr] gap-2 text-[8px]"><span>Sinal</span><b>{{ money(entryAmount, currency) }}</b><b>{{ money(previousEntryAmount, currency) }}</b><b :class="previousEntryAmount === '' ? 'text-[#8490ad]' : 'text-emerald-600'">{{ moneyDifference(entryAmount, previousEntryAmount) }}</b></div>
            <div class="mt-3 grid grid-cols-[0.9fr_1fr_1fr_0.75fr] gap-2 text-[8px]"><span>Validade</span><b>{{ date(expiresAt) }}</b><b>{{ date(previousExpiresAt) }}</b><b :class="previousExpiresAt ? 'text-emerald-600' : 'text-[#8490ad]'">{{ daysDifference(expiresAt, previousExpiresAt) }}</b></div>
            <p class="mt-auto border border-[#d7c8ff] py-2 text-center text-[8px] font-semibold text-[#5a24c7]">Histórico de contrapropostas indisponível nesta tela.</p>
          </article>
        </div>

        <aside class="space-y-4 pl-0.5">
          <article class="h-[279px] overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <header class="flex items-center justify-between">
              <h2 class="text-[11px] font-bold">Missões e conquistas</h2>
              <small class="text-[7px] text-[#8490ad]">Sem endpoint</small>
            </header>
            <div class="mt-5 flex items-center gap-3 border-b border-[#f0edf5] pb-4">
              <i class="grid size-10 place-items-center rounded-full bg-violet-50 text-[#5a24c7]"><i class="i-lucide-sparkles size-4" /></i>
              <span>
                <strong class="block text-[9px]">Recompensas indisponíveis</strong>
                <small class="mt-1 block text-[7px] text-[#69759c]">A API não retorna missões, pontos ou conquistas para propostas.</small>
              </span>
            </div>
            <div class="mt-5 rounded-xl bg-[#faf9fc] px-4 py-3">
              <div class="flex items-center justify-between text-[8px]"><span class="text-[#69759c]">Etapas concluídas</span><strong>{{ completedStageCount }}/{{ rawTimeline.length }}</strong></div>
              <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-[#ece8f4]"><i class="block h-full rounded-full bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf]" :class="progressClass(progressPercentage)" /></div>
              <small class="mt-3 block text-[7px] text-[#69759c]">{{ progressPercentage }}% conforme o cronograma retornado.</small>
            </div>
          </article>

          <article class="h-[326px] overflow-hidden rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_5px_20px_rgba(53,34,100,0.03)]">
            <header class="flex items-center justify-between">
              <h2 class="text-[11px] font-bold">Atividade</h2>
              <small class="text-[7px] text-[#7a85a2]">Timeline real</small>
            </header>
            <div
              v-for="slot in activitySlots"
              :key="slot.id"
              class="mt-3 flex gap-3 border-b border-[#f0edf5] pb-2 last:border-b-0 last:pb-0"
            >
              <i
                class="grid size-7 shrink-0 place-items-center rounded-full text-[8px] font-bold"
                :class="slot.unavailable ? 'border border-dashed border-[#d9d4e4] bg-[#faf9fc] text-[#99a2b8]' : 'bg-violet-50 text-[#5a24c7]'"
              >
                {{ slot.unavailable ? '—' : initials(slot.activity.actor) }}
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{ slot.unavailable ? 'Atividade indisponível' : slot.activity.actor }}</strong>
                <small class="mt-1 block truncate text-[7px] text-[#69759c]">{{ slot.unavailable ? 'A API não retornou outro evento.' : slot.activity.label }}</small>
              </span>
              <small class="shrink-0 text-[7px] text-[#8490ad]">{{ slot.unavailable ? '—' : slot.activity.date }}</small>
            </div>
            <p class="mt-3 flex h-8 items-center rounded-full border border-[#ece8f4] px-3 text-[7px] text-[#9aa2b9]">
              Comentários indisponíveis: não há contrato de envio nesta tela.
              <i class="i-lucide-message-circle-off ml-auto size-4" />
            </p>
          </article>
        </aside>
      </section>
    </template>

    <p v-if="model.localError || error" class="fixed bottom-7 right-7 z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600">{{ model.localError || error }}</p>
    <p v-else-if="model.notice" class="fixed bottom-7 right-7 z-50 max-w-sm rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[9px] leading-4 text-emerald-700">{{ model.notice }}</p>
  </main>
</template>
